import { useState } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Container, Row, Col } from 'reactstrap'
import s from './CreateContactModal.scss'
import { FaAngleRight, FaAngleDown } from 'react-icons/fa'
import {
  KeyValuePairs,
  InputContainer,
  GoogleAutocomplete,
  Checkbox
} from 'components/ideal-core-ui'
import { useImmer } from 'use-immer'
import { toCompaniesSelect } from 'components/Companies/serializers'
import { PaginatedSelect } from 'components/common/PaginatedSelect'
import { AutosuggestInput } from 'components/common/AutosuggestInput'
import { makePostRequest } from 'utils/api'

const CreateContactModal = ({
  toggleCreateContactModal,
  isCreateContactModalOpen,
  lead,
  createContact
}) => {
  const [isInfoSectionOpen, setIsInfoSectionOpen] = useState(true)
  const [isCreateContactSectionOpen, setIsCreateContactSectionOpen] = useState(true)
  const [prevLead, setPrevLead] = useState(lead)
  const [localLead, setLocalLead] = useImmer(lead)
  const [isAddCompanyVisible, setIsAddCompanyVisible] = useState(false)
  const [name, setName] = useState('')
  const [legalName, setLegalName] = useState('')
  const [hasDuplicateError, setHasDuplicateError] = useState(false)
  const [company, setCompany] = useState(null)
  const [shouldCopyNotes, setShouldCopyNotes] = useState(true)
  const [shouldCopyChecklist, setShouldCopyChecklist] = useState(true)
  const [shouldCopySpend, setShouldCopySpend] = useState(true)

  const resetCompany = () => {
    setName('')
    setLegalName('')
    setIsAddCompanyVisible(false)
  }

  if (prevLead !== lead) {
    setPrevLead(lead)
    setLocalLead(lead)
  }

  const onSelectCallback = params => {
    const { address, city, state, latitude, longitude, zipCode, country, street } = params
    setLocalLead(draft => {
      draft.address = address
      draft.city = city
      draft.state = state
      draft.latitude = latitude
      draft.longitude = longitude
      draft.zipCode = zipCode
      draft.country = country
      draft.street = street
    })
  }

  const validateProperty = (value, suggestions) => {
    const error = !!suggestions.filter(
      suggestion => suggestion.title.trim().toLowerCase() === value.trim().toLowerCase()
    ).length
    setHasDuplicateError(error)
  }

  const saveCompany = async () => {
    try {
      const formData = new FormData()
      formData.append('companyData', JSON.stringify({ name, legalName }))

      const response = await makePostRequest('/v1/companies', formData)
      setCompany({
        value: response.id,
        label: response.name
      })

      resetCompany()
      // eslint-disable-next-line no-empty
    } catch (e) {}
  }

  return (
    <>
      <Modal
        keyboard={false}
        backdrop="static"
        isOpen={isCreateContactModalOpen}
        toggle={toggleCreateContactModal}
        size="xl"
      >
        <ModalHeader toggle={toggleCreateContactModal} className={s.modalHeader}>
          Create Contact from Lead
        </ModalHeader>
        <ModalBody>
          <Container>
            <p>
              {!isInfoSectionOpen ? (
                <FaAngleRight
                  style={{ cursor: 'pointer' }}
                  onClick={() => setIsInfoSectionOpen(!isInfoSectionOpen)}
                />
              ) : (
                <FaAngleDown
                  style={{ cursor: 'pointer' }}
                  onClick={() => setIsInfoSectionOpen(!isInfoSectionOpen)}
                />
              )}{' '}
              <span className={s.category}>Lead Information</span>
            </p>
            {isInfoSectionOpen && (
              <Row style={{ marginBottom: 30 }}>
                <Col>
                  <div className={s.leadInfo}>
                    <p>
                      <span className={s.leadInfoLabel}>First:</span>
                      <span className={s.leadInfoValue}>{lead.contactFirstName}</span>
                    </p>
                    <p>
                      <span className={s.leadInfoLabel}>Last:</span>
                      <span className={s.leadInfoValue}>{lead.contactLastName}</span>
                    </p>
                    <p>
                      <span className={s.leadInfoLabel}>Email:</span>
                      <span className={s.leadInfoValue}>{lead.email}</span>
                    </p>
                    <p>
                      <span className={s.leadInfoLabel}>Phone:</span>
                      <span className={s.leadInfoValue}>{lead.phone}</span>
                    </p>
                    <p>
                      <span className={s.leadInfoLabel}>Title:</span>
                      <span className={s.leadInfoValue}>{lead.title}</span>
                    </p>
                    <p>
                      <span className={s.leadInfoLabel}>Company:</span>
                      <span className={s.leadInfoValue}>{lead.company}</span>
                    </p>
                  </div>
                </Col>
                <Col>
                  <div className={s.leadInfo}>
                    <div>
                      <span className={s.leadInfoLabel}>Address</span>
                      <p className={s.leadInfoValue}>{lead.address}</p>
                      <p className={s.leadInfoValue}>{lead.address2}</p>
                    </div>
                  </div>
                </Col>
                <div className={s.aboutInfo}>
                  <p>
                    <span className={s.leadInfoLabel}>About:</span>
                    <span className={s.leadInfoValue}>{lead.about}</span>
                  </p>
                </div>
              </Row>
            )}
            <p>
              {!isCreateContactSectionOpen ? (
                <FaAngleRight
                  style={{ cursor: 'pointer' }}
                  onClick={() => setIsCreateContactSectionOpen(!isCreateContactSectionOpen)}
                />
              ) : (
                <FaAngleDown
                  style={{ cursor: 'pointer' }}
                  onClick={() => setIsCreateContactSectionOpen(!isCreateContactSectionOpen)}
                />
              )}{' '}
              <span className={s.category}>Create Contact from Lead</span>
            </p>
            {isCreateContactSectionOpen && (
              <>
                <Row>
                  <Col>
                    <div className={s.leadInfo}>
                      <KeyValuePairs
                        pairs={[
                          {
                            key: '* First Name',
                            keyClass: [s.label],
                            valueClass: [s.value],
                            value: (
                              <InputContainer
                                type="text"
                                placeholder="Enter First Name"
                                cb={value =>
                                  setLocalLead(draft => {
                                    draft.contactFirstName = value
                                  })
                                }
                                text={localLead.contactFirstName}
                              />
                            )
                          },
                          {
                            key: '* Last Name',
                            keyClass: [s.label],
                            valueClass: [s.value],
                            value: (
                              <InputContainer
                                type="text"
                                placeholder="Enter Last Name"
                                cb={value =>
                                  setLocalLead(draft => {
                                    draft.contactLastName = value
                                  })
                                }
                                text={localLead.contactLastName}
                              />
                            )
                          },
                          {
                            key: '* Email',
                            keyClass: [s.label],
                            valueClass: [s.value],
                            value: (
                              <InputContainer
                                type="email"
                                placeholder="Enter Email"
                                cb={value =>
                                  setLocalLead(draft => {
                                    draft.email = value
                                  })
                                }
                                text={localLead.email}
                              />
                            )
                          },
                          {
                            key: (
                              <>
                                <span>* Company</span>
                                <span
                                  className={s.addCompany}
                                  onClick={() => setIsAddCompanyVisible(true)}
                                >
                                  Add New Company
                                </span>
                              </>
                            ),
                            value: (
                              <PaginatedSelect
                                url="/v1/companies"
                                value={company}
                                onChange={value => setCompany(value)}
                                serializer={toCompaniesSelect}
                              />
                            )
                          },
                          isAddCompanyVisible
                            ? {
                                key: <span className={s.addCompanyTitle}>Add Company</span>,
                                sectionClass: [s.addCompanySection],
                                value: (
                                  <>
                                    {hasDuplicateError && (
                                      <p className={s.error}>
                                        Another company with the same name already exists
                                      </p>
                                    )}
                                    <AutosuggestInput
                                      type="COMPANY"
                                      placeholder="*Company Name"
                                      onChangeCb={(value, suggestions) => {
                                        setName(value)
                                        validateProperty(value, suggestions)
                                      }}
                                      length="3"
                                      className={s.companyAutosuggest}
                                      initialValue={name}
                                      cy="company_name_input"
                                    />
                                    <InputContainer
                                      type="text"
                                      classname={s.addCompanyInput}
                                      placeholder="Company Legal Name"
                                      cb={setLegalName}
                                      text={legalName}
                                      cy="company_legal_name_input"
                                    />
                                    <Button
                                      disabled={!name || !legalName}
                                      className={s.saveBtn}
                                      onClick={saveCompany}
                                    >
                                      Save Company
                                    </Button>
                                  </>
                                )
                              }
                            : null,
                          {
                            key: 'Phone',
                            keyClass: [s.label],
                            valueClass: [s.value],
                            value: (
                              <InputContainer
                                type="text"
                                placeholder="Enter Phone"
                                cb={value =>
                                  setLocalLead(draft => {
                                    draft.phone = value
                                  })
                                }
                                text={localLead.phone}
                              />
                            )
                          },
                          {
                            key: 'Title',
                            keyClass: [s.label],
                            valueClass: [s.value],
                            value: (
                              <InputContainer
                                type="text"
                                placeholder="Enter Title"
                                cb={value =>
                                  setLocalLead(draft => {
                                    draft.title = value
                                  })
                                }
                                text={localLead.title}
                              />
                            )
                          }
                        ]}
                      />
                    </div>
                  </Col>
                  <Col>
                    <div className={s.leadInfo}>
                      <KeyValuePairs
                        pairs={[
                          {
                            key: 'Address',
                            value: (
                              <GoogleAutocomplete
                                value={localLead.address}
                                onChange={value =>
                                  setLocalLead(draft => {
                                    draft.address = value
                                  })
                                }
                                onSelectCallback={onSelectCallback}
                              />
                            )
                          },
                          {
                            key: 'Street Address',
                            value: (
                              <InputContainer
                                type="text"
                                text={localLead.address2}
                                placeholder="Street 1"
                                cb={value =>
                                  setLocalLead(draft => {
                                    draft.address2 = value
                                  })
                                }
                              />
                            )
                          },
                          {
                            key: 'City',
                            value: (
                              <InputContainer
                                type="text"
                                text={localLead.city}
                                placeholder="Enter City"
                                cb={value =>
                                  setLocalLead(draft => {
                                    draft.city = value
                                  })
                                }
                              />
                            )
                          },
                          {
                            key: 'Region',
                            value: (
                              <InputContainer
                                type="text"
                                text={localLead.region}
                                placeholder="Enter Region"
                                cb={value =>
                                  setLocalLead(draft => {
                                    draft.region = value
                                  })
                                }
                              />
                            )
                          },
                          {
                            key: 'Zip/Postal Code',
                            value: (
                              <InputContainer
                                type="text"
                                text={localLead.zipCode}
                                placeholder="Enter Zip/Postal Code"
                                cb={value =>
                                  setLocalLead(draft => {
                                    draft.zipCode = value
                                  })
                                }
                              />
                            )
                          },
                          {
                            key: 'Country',
                            value: (
                              <InputContainer
                                type="text"
                                text={localLead.country}
                                placeholder="Enter Country"
                                cb={value =>
                                  setLocalLead(draft => {
                                    draft.country = value
                                  })
                                }
                              />
                            )
                          }
                        ]}
                      />
                    </div>
                  </Col>
                  <KeyValuePairs
                    style={{ padding: '0 42px' }}
                    pairs={[
                      {
                        key: 'About',
                        keyClass: [s.aboutLabel],
                        valueClass: [s.valueLabel],
                        value: (
                          <InputContainer
                            type="textarea"
                            classname={s.aboutInput}
                            placeholder="Enter about info"
                            cb={value =>
                              setLocalLead(draft => {
                                draft.about = value
                              })
                            }
                            text={localLead.about}
                          />
                        )
                      }
                    ]}
                  />
                </Row>
                <div className={s.checkboxWrapper}>
                  <Checkbox
                    isChecked={shouldCopyNotes}
                    triggerCheckbox={value => setShouldCopyNotes(value.target.checked)}
                  />
                  <p>Copy Lead Notes to Contact Notes</p>
                  <br />
                  <Checkbox
                    isChecked={shouldCopyChecklist}
                    triggerCheckbox={value => setShouldCopyChecklist(value.target.checked)}
                  />
                  <p>Copy Lead Checklists to Contact Checklists</p>
                  <br />
                  <Checkbox
                    isChecked={shouldCopySpend}
                    triggerCheckbox={value => setShouldCopySpend(value.target.checked)}
                  />
                  <p>Copy Lead Spend to Contact Spend</p>
                </div>
              </>
            )}
          </Container>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleCreateContactModal}>
            Cancel
          </Button>
          <Button
            className={s.saveBtn}
            onClick={() => {
              createContact(
                {
                  ...localLead,
                  company: {
                    id: company.value,
                    name: company.label
                  }
                },
                {
                  shouldCopyChecklist,
                  shouldCopyNotes,
                  shouldCopySpend
                }
              )
            }}
            disabled={
              !localLead.contactFirstName ||
              !localLead.contactLastName ||
              !localLead.email ||
              !company
            }
          >
            Create Contact
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default CreateContactModal
